import clsx from 'clsx';
import { ChevronDown, Download, FileText, MessageSquare, Mic, Paperclip, Send, Video, Volume2, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, PTTIcon, PTVIcon } from '@/components';
import { useERSContext } from '@/context';
import { useAIProvider } from '@/hooks/useAIProvider';
import { constants } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { ChatMessage } from './ChatMessage';
import { MessageSkeleton } from '@/components/shared/loader';
import PDFViewer from '@/components/shared/PDFViewer/PDFViewer';

export const SelectedChat = ({ selectedChat, messageToScrollTo, onMessageScrolled }) => {
  const { t, i18n } = useTranslation();
  const { chatHistory, initialFetchChatHistory, setUnreadInfo, setChatHistory, incidents } = useERSContext();
  const { callTranscribeAudio } = useAIProvider({ temperature: 0.6 });

  const { callAPI } = useAIProvider({
    model: 'gpt-4o-mini',
    temperature: 0.3,
  });

  const resolvedStatus = incidents.filter(incident => {
    const chatIdBeforeAt = selectedChat?.id.split('@')[0];
    return incident.id === chatIdBeforeAt;
  });

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isPttActive, setIsPttActive] = useState(false);
  const [isPttLocked, setIsPttLocked] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const [pttAudioBlob, setPttAudioBlob] = useState(null);
  const [pttAudioUrl, setPttAudioUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isScrollingToMessage, setIsScrollingToMessage] = useState(false);
  const [isFromSearch, setIsFromSearch] = useState(false);
  const [translatedMessages, setTranslatedMessages] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const micButtonRef = useRef(null);
  const dragStartY = useRef(0);
  const dropZoneRef = useRef(null);
  const idRef = useRef(null);
  const messageRefs = useRef({});
  const scrollTimeoutRef = useRef(null);

  // Clear translations when language changes
  useEffect(() => {
    setTranslatedMessages({});
  }, [i18n.language]);

  useEffect(() => {
    translateChatContent();
  }, [messages, i18n.language]);

  const translateChatContent = async () => {
    if (!messages || messages.length === 0 || i18n.language === 'en') {
      setIsTranslating(false);
      return;
    }

    // Filter only text messages that need translation
    const textMessages = messages.filter(message => message.text && message.text.trim() && message.type === 'text');

    if (textMessages.length === 0) {
      setIsTranslating(false);
      return;
    }

    setIsTranslating(true);
    try {
      const texts = textMessages.map(message => message.text);
      const prompt = `Translate the following texts into ${i18n.language}. Do NOT add any quotes, punctuation, or extra context. Provide ONLY the plain translations in the exact same order, separated by "|||":\n\n${texts.join('\n')}`;

      const translated = await callAPI([{ role: 'user', content: prompt }]);
      const translationsArray = translated.split('|||').map(t =>
        t
          .replace(/^["'`]|["'`]$/g, '')
          .replace(/^["'`]|["'`]$/g, '')
          .trim()
      );

      const newTranslations = {};
      textMessages.forEach((message, index) => {
        if (translationsArray[index] && translationsArray[index] !== message.text) {
          newTranslations[message.id] = translationsArray[index];
        }
      });

      setTranslatedMessages(newTranslations);
    } catch (err) {
      console.error('Batch message translation failed:', err);
      // On error, clear translations to show original text
      setTranslatedMessages({});
    } finally {
      setIsTranslating(false);
    }
  };

  // Check if a specific message has translations ready
  const isMessageTranslated = message => {
    if (!message.text || message.type !== 'text') return true;
    return translatedMessages[message.id] !== undefined;
  };

  // Get messages that need translation (for skeleton display)
  const getUntranslatedMessages = useMemo(() => {
    if (!isTranslating) return new Set();

    const untranslated = new Set();
    messages.forEach(message => {
      if (message.text && message.text.trim() && message.type === 'text' && !isMessageTranslated(message)) {
        untranslated.add(message.id);
      }
    });
    return untranslated;
  }, [messages, isTranslating, translatedMessages]);

  const isMessageFromMe = useCallback(msg => {
    return msg.model?.attributes?.sender === constants.CHAT_PANEL.ME ? constants.CHAT_PANEL.ME : 'them';
  }, []);

  // Helper function to determine message type from SDK message
  const getMessageType = useCallback(msg => {
    if (msg.mediaType) {
      switch (msg.mediaType) {
        case constants.CHAT_PANEL.IMAGE:
          return constants.CHAT_PANEL.IMAGE;
        case constants.CHAT_PANEL.VIDEO:
          return constants.CHAT_PANEL.VIDEO;
        case constants.CHAT_PANEL.AUDIO:
          return constants.CHAT_PANEL.AUDIO;
        default:
          return constants.CHAT_PANEL.FILE;
      }
    }
    // If no mediaType, check attachment file extension
    if (msg.attachment || msg.attachmentDescription) {
      const fileName = msg.attachmentDescription || msg.attachment || '';
      const ext = fileName.toLowerCase().split('.').pop();
      if (['mp3', 'wav', 'ogg', 'webm', 'm4a', 'aac'].includes(ext)) {
        return constants.CHAT_PANEL.AUDIO;
      }
      if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) {
        return constants.CHAT_PANEL.VIDEO;
      }
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) {
        return constants.CHAT_PANEL.IMAGE;
      }
      if (fileName) {
        return constants.CHAT_PANEL.FILE;
      }
    }
    return constants.CHAT_PANEL.TEXT;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [shouldAutoScroll]);

  // Function to scroll to a specific message
  const scrollToMessage = useCallback(
    messageId => {
      // Clear any existing timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      setIsScrollingToMessage(true);
      setShouldAutoScroll(false);

      const messageElement = messageRefs.current[messageId];
      if (messageElement && messagesContainerRef.current) {
        console.log('Scrolling to searched message:', messageId);
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Highlight the message temporarily
        messageElement.classList.add('bg-highlight');

        // Keep auto-scroll disabled for longer when coming from search
        const timeoutDuration = isFromSearch ? 5000 : 3000;

        scrollTimeoutRef.current = setTimeout(() => {
          messageElement.classList.remove('bg-highlight');
          setIsScrollingToMessage(false);

          // Only re-enable auto-scroll if NOT coming from search
          if (!isFromSearch) {
            setShouldAutoScroll(true);
          }

          onMessageScrolled?.();
        }, timeoutDuration);
      } else {
        scrollTimeoutRef.current = setTimeout(() => {
          setIsScrollingToMessage(false);
          if (!isFromSearch) {
            setShouldAutoScroll(true);
          }
          onMessageScrolled?.();
        }, 1000);
      }
    },
    [onMessageScrolled, isFromSearch]
  );

  useEffect(() => {
    if (messageToScrollTo) {
      console.log('Coming from search, disabling auto-scroll');
      setIsFromSearch(true);
      setShouldAutoScroll(false);

      console.log('messageToScrollTo changed:', messageToScrollTo);
      const timer = setTimeout(() => {
        scrollToMessage(messageToScrollTo);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setIsScrollingToMessage(false);
      // Don't immediately re-enable auto-scroll, let user decide
    }
  }, [messageToScrollTo, scrollToMessage]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || !selectedChat) return;

    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setShowScrollButton(!isNearBottom);

    // Only update auto-scroll preference if not from search and not scrolling to message
    if (!isScrollingToMessage && !messageToScrollTo && !isFromSearch) {
      setShouldAutoScroll(isNearBottom);
    }

    if (isNearBottom) {
      const theID = idRef.current;
      setChatHistory(prevChatHistory => {
        if (prevChatHistory[theID] && prevChatHistory[theID].chatBox) {
          prevChatHistory[theID].chatBox.markAllMessagesAsRead();
        }
        return prevChatHistory;
      });
    }
  }, [selectedChat, setUnreadInfo, setChatHistory, isScrollingToMessage, messageToScrollTo, isFromSearch]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Format messages from chat data
  const formatMessages = useCallback(
    chatData => {
      if (!chatData?.messages) return [];

      return chatData.messages.map(msg => {
        const messageType = getMessageType(msg);
        return {
          id: msg.id || Date.now().toString(),
          displayName: msg.displayName,
          sender: isMessageFromMe(msg) === constants.CHAT_PANEL.ME ? constants.CHAT_PANEL.ME : msg.sender || 'Unknown',
          text: msg.text || msg.body || '',
          time: msg.sendingDate,
          type: messageType,
          fileUrl: msg.attachment,
          fileName: msg.attachmentDescription,
          nickname: msg?.nickname,
          isPlaybackMessage: msg?.isPlaybackMessage,
          duration: msg?.duration,
          glLat: msg?.glLat,
          glLon: msg?.glLon,
        };
      });
    },
    [isMessageFromMe, getMessageType]
  );

  // Handle file processing
  const processFiles = useCallback(
    files => {
      if (files.length === 0) return;

      // Clean up previous URLs safely
      attachedFiles.forEach(file => {
        if (file.url?.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
      });

      const file = files[0];
      const fileType = getFileType(file.type);
      const fileUrl = URL.createObjectURL(file);

      setAttachedFiles([
        {
          name: file.name,
          type: fileType,
          url: fileUrl,
          file,
        },
      ]);
    },
    [attachedFiles]
  );

  const handleDroppedFiles = useCallback(
    files => {
      processFiles(files);
    },
    [processFiles]
  );

  const fetchMessages = async () => {
    if (!selectedChat || !selectedChat.id) return;

    try {
      await initialFetchChatHistory(selectedChat);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setErrorMessage('Failed to load messages. Please try again.');
      setShowError(true);
    }
  };

  // Auto-scroll for new messages and when opening chats
  useEffect(() => {
    if (!isTranslating && messages.length > 0 && shouldAutoScroll) {
      // Small timeout to ensure the DOM has updated with the translated content
      const timer = setTimeout(() => {
        if (!isScrollingToMessage && !messageToScrollTo && !isFromSearch) {
          messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isTranslating, messages, shouldAutoScroll, isScrollingToMessage, messageToScrollTo, isFromSearch]);

  // handles showError timeout, scroll listener, cleanup, and drag/drop
  useEffect(() => {
    //Auto-hide error toast
    let errorTimer;
    if (showError) {
      errorTimer = setTimeout(() => setShowError(false), 6000);
    }

    // Scroll event listener
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleScroll);
    }

    //Cleanup blob URLs when component unmounts
    const revokeBlobs = () => {
      if (pttAudioUrl) URL.revokeObjectURL(pttAudioUrl);
      attachedFiles.forEach(file => {
        if (file.url?.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
      });
    };

    // Drag and drop events for file input area
    const handleDragOver = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragEnter = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = e => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setIsDragOver(false);
      }
    };

    const handleDrop = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleDroppedFiles(files);
      }
    };

    // Apply drag and drop to both message area and input area
    const dropZones = [messagesContainerRef.current, dropZoneRef.current].filter(Boolean);

    dropZones.forEach(zone => {
      if (zone) {
        zone.addEventListener('dragover', handleDragOver);
        zone.addEventListener('dragenter', handleDragEnter);
        zone.addEventListener('dragleave', handleDragLeave);
        zone.addEventListener('drop', handleDrop);
      }
    });

    //Cleanup everything
    return () => {
      if (errorTimer) clearTimeout(errorTimer);
      revokeBlobs();
      if (messagesContainer) messagesContainer.removeEventListener('scroll', handleScroll);

      dropZones.forEach(zone => {
        if (zone) {
          zone.removeEventListener('dragover', handleDragOver);
          zone.removeEventListener('dragenter', handleDragEnter);
          zone.removeEventListener('dragleave', handleDragLeave);
          zone.removeEventListener('drop', handleDrop);
        }
      });
    };
  }, [showError, handleScroll, pttAudioUrl, attachedFiles, handleDroppedFiles]);

  // handles chat selection + unread marking
  useEffect(() => {
    if (!selectedChat) return;
    idRef.current = selectedChat.id;

    const chatData = chatHistory[selectedChat.id];
    if (chatData) {
      const formattedMessages = formatMessages(chatData);
      setMessages(formattedMessages);

      // Only auto-scroll to bottom if NOT coming from search and NOT scrolling to specific message
      if (!messageToScrollTo && !isFromSearch) {
        setTimeout(() => {
          setShouldAutoScroll(true);
          messagesEndRef.current?.scrollIntoView({
            behavior: 'auto',
            block: 'end',
          });
        }, 300);
      } else {
        setShouldAutoScroll(false);
      }
    } else {
      fetchMessages();
    }

    // Reset states when switching chats - but respect search state
    if (!messageToScrollTo && !isFromSearch) {
      setShouldAutoScroll(true);
    }

    // Mark as read if near bottom
    if (messagesContainerRef.current) {
      const el = messagesContainerRef.current;
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;

      if (isNearBottom) {
        const theID = selectedChat.id;

        setChatHistory(prevChatHistory => {
          if (prevChatHistory[theID]?.chatBox) {
            prevChatHistory[theID].chatBox.markAllMessagesAsRead();
          }
          return prevChatHistory;
        });
      }
    }
  }, [selectedChat, chatHistory, formatMessages, setUnreadInfo, setChatHistory, messageToScrollTo, isFromSearch]);

  // Helper function to format message time
  const formatMessageTime = useCallback(timestamp => {
    if (!timestamp) return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  }, []);

  // Helper function to format date headers
  const formatDateHeader = useCallback(timestamp => {
    if (!timestamp) return '';

    const messageDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const normalizeDate = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const normalizedMessageDate = normalizeDate(messageDate);
    const normalizedToday = normalizeDate(today);
    const normalizedYesterday = normalizeDate(yesterday);

    if (normalizedMessageDate.getTime() === normalizedToday.getTime()) {
      return 'Today';
    }

    if (normalizedMessageDate.getTime() === normalizedYesterday.getTime()) {
      return 'Yesterday';
    }

    const getWeekStart = date => {
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      return new Date(date.setDate(diff));
    };

    const startOfWeek = getWeekStart(new Date(today));
    if (normalizedMessageDate >= normalizeDate(startOfWeek)) {
      return messageDate.toLocaleDateString('en-US', { weekday: 'long' });
    }

    const dd = String(messageDate.getDate()).padStart(2, '0');
    const mm = String(messageDate.getMonth() + 1).padStart(2, '0');
    const yyyy = messageDate.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }, []);

  // Function to group messages by date
  const groupMessagesByDate = useCallback(
    messages => {
      if (!messages || messages.length === 0) return [];

      const groupedMessages = [];
      let currentDateGroup = null;

      messages.forEach(message => {
        const messageDate = new Date(message.time).toDateString();

        if (!currentDateGroup || currentDateGroup.date !== messageDate) {
          currentDateGroup = {
            date: messageDate,
            displayDate: formatDateHeader(message.time),
            messages: [],
            nickname: message?.nickname,
          };
          groupedMessages.push(currentDateGroup);
        }

        // Get translated text for this message
        const translatedText = translatedMessages[message.id] || message.text;

        // Create message object with translated text
        const translatedMessage = {
          ...message,
          translatedText: translatedText,
        };

        currentDateGroup.messages.push(translatedMessage);
      });

      return groupedMessages;
    },
    [formatDateHeader, translatedMessages]
  );

  const handleFileInputChange = useCallback(
    event => {
      const files = Array.from(event.target.files);
      processFiles(files);
      event.target.value = '';
    },
    [processFiles]
  );

  const handleSendMessage = async () => {
    if (newMessage.trim() || attachedFiles.length > 0) {
      try {
        const isGroup = selectedChat.type === 'group';
        const chatBox = await wbSdk.chatManager.getChatBox(selectedChat.id, isGroup);

        if (attachedFiles.length > 0) {
          const filesToSend = attachedFiles.map(file => file.file);
          await chatBox.sendMessage(newMessage.trim(), filesToSend);
        } else {
          await chatBox.sendMessage(newMessage.trim());
        }

        setNewMessage('');
        setAttachedFiles([]);

        // Only auto-scroll if not from search and not scrolling to message
        if (!isScrollingToMessage && !messageToScrollTo && !isFromSearch) {
          setShouldAutoScroll(true);
        }
        await fetchMessages();
      } catch (error) {
        console.error('Error sending message:', error);
        setErrorMessage('Failed to send message. Please try again.');
        setShowError(true);
      }
    }
  };

  // PTT Functions
  const startPtt = () => {
    if (!selectedChat) return;

    try {
      setIsPttActive(true);

      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
          const mediaRecorder = new MediaRecorder(stream);
          const audioChunks = [];

          mediaRecorder.ondataavailable = event => {
            audioChunks.push(event.data);
          };

          mediaRecorder.onstop = () => {
            const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
            setPttAudioBlob(audioBlob);
            handleSendPttAudio(audioBlob);

            stream.getTracks().forEach(track => track.stop());
          };

          mediaRecorder.start();
        });
      }

      wbSdk.sipManager.openPtt(selectedChat.type, selectedChat.id, selectedChat.displayName, false);
    } catch (error) {
      console.error('Error starting PTT:', error);
      setIsPttActive(false);
    }
  };

  const stopPtt = async () => {
    if (!selectedChat) return;

    try {
      setIsPttActive(false);
      setIsPttLocked(false);
      setDragDistance(0);

      const audioData = await wbSdk.sipManager.closePtt(selectedChat.id);

      if (audioData && audioData.blob) {
        await handleSendPttAudio(audioData.blob);
      }

      if (pttAudioUrl) {
        URL.revokeObjectURL(pttAudioUrl);
        setPttAudioUrl(null);
      }
      setPttAudioBlob(null);
    } catch (error) {
      console.error('Error stopping PTT:', error);
    }
  };

  const lockPtt = () => {
    setIsPttLocked(true);
  };

  const unlockPtt = async () => {
    setIsPttLocked(false);
    await stopPtt();
  };

  // Mouse events for PTT
  const handleMicMouseDown = e => {
    if (!selectedChat) return;
    dragStartY.current = e.clientY;
    startPtt();
  };

  const handleMicMouseUp = () => {
    if (!isPttLocked && isPttActive) {
      stopPtt();
    }
  };

  const handleMicMouseMove = e => {
    if (isPttActive && !isPttLocked) {
      const currentY = e.clientY;
      const distance = dragStartY.current - currentY;
      setDragDistance(Math.max(0, distance));

      if (distance >= 40) {
        lockPtt();
      }
    }
  };

  const handleMicMouseLeave = () => {
    if (!isPttLocked && isPttActive) {
      stopPtt();
    }
  };

  const handleMicTouchStart = e => {
    if (!selectedChat) return;
    e.preventDefault();
    dragStartY.current = e.touches[0].clientY;
    startPtt();
  };

  const handleMicTouchMove = e => {
    if (isPttActive && !isPttLocked) {
      const currentY = e.touches[0].clientY;
      const distance = dragStartY.current - currentY;
      setDragDistance(Math.max(0, distance));

      if (distance >= 80) {
        lockPtt();
      }
    }
  };

  const handleMicTouchEnd = e => {
    e.preventDefault();
    if (!isPttLocked && isPttActive) {
      stopPtt();
    }
  };

  const handleSendPttAudio = async audioBlob => {
    if (!selectedChat || !audioBlob) return;

    let audioUrl;

    try {
      let transcription = '';

      try {
        transcription = await transcribeAudio(audioBlob);
        console.log('Audio transcription:', transcription);
      } catch (err) {
        console.warn('Transcription failed, sending audio without text:', err);
      }

      const isGroup = selectedChat.type === 'group';
      const chatBox = await wbSdk.chatManager.getChatBox(selectedChat.id, isGroup);

      const audioFile = new File([audioBlob], `ptt-audio-${Date.now()}.webm`, {
        type: audioBlob.type,
      });

      await chatBox.sendMessage(transcription, [audioFile]);

      // Create audio URL
      audioUrl = URL.createObjectURL(audioBlob);

      const message = {
        id: Date.now().toString(),
        sender: constants.CHAT_PANEL.ME,
        text: transcription || 'Audio message',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        type: constants.CHAT_PANEL.AUDIO,
        fileUrl: audioUrl,
        fileName: audioFile.name,
        hasTranscription: !!transcription,
      };

      setMessages(prev => [...prev, message]);

      // Only auto-scroll if not from search and not scrolling to message
      if (!isScrollingToMessage && !messageToScrollTo && !isFromSearch) {
        setShouldAutoScroll(true);
      }
      await fetchMessages();

      // ✅ Schedule URL cleanup after UI renders
      setTimeout(() => {
        URL.revokeObjectURL(audioUrl);
      }, 5000); // small delay so UI can still use it briefly
    } catch (error) {
      console.error('Error sending PTT audio:', error);
      if (audioUrl) URL.revokeObjectURL(audioUrl); // cleanup even on error
    }
  };

  const transcribeAudio = async audioBlob => {
    try {
      const audioFile = new File([audioBlob], 'audio.webm', {
        type: 'audio/webm',
      });

      const response = await callTranscribeAudio(audioFile, {
        model: 'whisper-1',
        language: 'en',
        response_format: constants.CHAT_PANEL.TEXT,
      });

      return response;
    } catch (error) {
      console.error('OpenAI transcription error:', error);
      throw error;
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getFileType = mimeType => {
    if (mimeType.startsWith('image/')) return constants.CHAT_PANEL.IMAGE;
    if (mimeType.startsWith('video/')) return constants.CHAT_PANEL.VIDEO;
    if (mimeType.startsWith('audio/')) return constants.CHAT_PANEL.AUDIO;
    return constants.CHAT_PANEL.FILE;
  };

  const removeAttachedFile = useCallback(index => {
    setAttachedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      // Revoke URL for removed file
      if (prev[index]?.url && prev[index].url.startsWith('blob:')) {
        URL.revokeObjectURL(prev[index].url);
      }
      return newFiles;
    });
  }, []);

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadAttachedFile = useCallback(file => {
    if (!file?.url) return;
    try {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name || `download-${Date.now()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      window.open(file.url, '_blank');
    }
  }, []);

  // Add a function to manually re-enable auto-scroll
  const enableAutoScroll = useCallback(() => {
    setShouldAutoScroll(true);
    setIsFromSearch(false);
    scrollToBottom();
  }, [scrollToBottom]);

  // Memoized PTT Status component
  const PttStatus = useMemo(
    () => (
      <div className=" flex items-center justify-between bg-success-muted border border-success-muted rounded-lg px-3 py-2 h-12">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-3 h-3 border-success-muted rounded-full animate-pulse flex-shrink-0"></div>
          <div className="flex items-center gap-2 flex-1">
            <span className="text-success-strong font-medium text-sm">
              {isPttLocked ? t('CHAT_PANEL.PTT_LOCKED') : t('CHAT_PANEL.PTT_ACTIVE')}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isPttLocked ? (
            <button
              onClick={unlockPtt}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-error-strong text-inverse rounded transition-colors"
            >
              <X size={14} />
              {t('CHAT_PANEL.PTT_RELEASE_AND_SEND')}
            </button>
          ) : (
            <>
              <span className="text-xs text-success-strong hidden sm:block">{t('CHAT_PANEL.PTT_SLIDE_HINT')}</span>
              <button
                className="w-6 h-6 bg-error-strong rounded-full flex items-center justify-center cursor-pointer flex-shrink-0"
                onClick={stopPtt}
              >
                <X size={12} color="white" />
              </button>
            </>
          )}
        </div>
      </div>
    ),
    [isPttLocked, t, unlockPtt, stopPtt]
  );

  // Memoized Date Separator Component
  const DateSeparator = useMemo(
    () =>
      ({ date }) => {
        // Check if the date matches any of the translatable English values
        if (constants.TRANSLATABLE_VALUES.includes(date)) {
          let translationKey;

          if (date === 'Today') {
            translationKey = 'today';
          } else if (date === 'Yesterday') {
            translationKey = 'yesterday';
          } else {
            // It's a day of week - find its index
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayIndex = days.indexOf(date);
            translationKey = `days.${dayIndex}`;
          }

          return (
            <div className="flex items-center justify-center my-4">
              <div className="text-text">{t(translationKey)}</div>
            </div>
          );
        }

        // For all other dates (like "11/10/2025"), keep as is
        return (
          <div className="flex items-center justify-center my-4">
            <div className="text-text">{date}</div>
          </div>
        );
      },
    [t]
  );

  // Memoized grouped messages
  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages, groupMessagesByDate]);

  // Memoized PTT button transform
  const pttButtonTransform = useMemo(() => {
    if (isPttActive && !isPttLocked) {
      return `translateY(-${Math.min(30, dragDistance / 2)}px)`;
    }
    return '';
  }, [isPttActive, isPttLocked, dragDistance]);

  return (
    <div
      ref={dropZoneRef}
      className={cn(
        'flex flex-col w-full h-full relative z-[9999]',
        isDragOver && 'bg-info-muted-hover border-2 border-dashed border-secondary'
      )}
    >
      <div className="flex flex-col flex-1 w-full h-full overflow-hidden">
        {/* Scrollable message list */}
        <div ref={messagesContainerRef} className="flex flex-col flex-1 overflow-y-auto px-4 pt-4 pb-2 space-y-4">
          {messages && messages.length > 0 ? (
            groupedMessages.map((dateGroup, groupIndex) => (
              <div key={groupIndex} className="space-y-2">
                <DateSeparator date={dateGroup.displayDate} />
                {dateGroup.messages.map(message => {
                  // Show skeleton only for messages that are being translated
                  const showSkeleton = isTranslating && getUntranslatedMessages.has(message.id);

                  return showSkeleton ? (
                    <MessageSkeleton
                      key={`skeleton-${message.id}`}
                      isOwnMessage={message.sender === constants.CHAT_PANEL.ME}
                    />
                  ) : (
                    <div
                      key={message.id}
                      ref={el => {
                        if (el) {
                          messageRefs.current[message.id] = el;
                        } else {
                          delete messageRefs.current[message.id];
                        }
                      }}
                      className={cn(message.id === messageToScrollTo && 'bg-highlight transition-colors duration-2000')}
                    >
                      <ChatMessage
                        id={message.id}
                        sender={message.sender}
                        displayName={message.displayName}
                        text={message.translatedText} // Use translatedText here
                        time={formatMessageTime(message.time)}
                        type={message.type}
                        fileUrl={message.fileUrl}
                        fileName={message.fileName}
                        isHighlighted={message.id === messageToScrollTo}
                        isAudioWithTranscription={message.hasTranscription}
                        userId={selectedChat?.id}
                        nickname={message?.nickname}
                        userType={selectedChat?.type === 'group' ? 'group' : 'user'}
                        isPlaybackMessage={message.isPlaybackMessage}
                        duration={message.duration}
                        glLat={message.glLat}
                        glLon={message.glLon}
                      />
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center text-center text-muted-foreground h-full">
              <MessageSquare size={120} absoluteStrokeWidth />
              <h2 className="text-xl font-semibold mt-4">{t('CHAT_PANEL.NO_CHAT_MESSAGE')}</h2>
              <p className="text-sm text-muted mt-2 max-w-xs">{t('CHAT_PANEL.FALLBACK_MESSAGE')}</p>
            </div>
          )}

          {/* Auto-scroll anchor */}
          {!isScrollingToMessage && !messageToScrollTo && !isFromSearch && <div ref={messagesEndRef} />}
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && attachedFiles.length === 0 && (
        <button
          onClick={enableAutoScroll}
          className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-primary text-inverse p-2 rounded-full shadow-lg z-30"
        >
          <ChevronDown size={20} />
        </button>
      )}

      {/* Attached Files Preview */}
      {attachedFiles.length > 0 && (
        <div className="absolute left-0 right-0 border-t border-b p-3 bg shadow-lg h-full z-10">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setAttachedFiles([])}>
              <X size={20} className="text-muted" />
            </button>
            <h3 className="text-sm font-semibold">{t('CHAT_PANEL.ATTACHED_FILES_TITLE')}</h3>
            <button onClick={() => handleDownloadAttachedFile(attachedFiles[0])}>
              <Download size={20} className="text-muted" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {attachedFiles.map((file, index) => (
              <div key={index} className="flex flex-col  items-center w-full ">
                {file.type === t('CHAT_PANEL.IMAGE') && (
                  <img src={file.url} alt={file.name} className="w-full h-full object-contain rounded" />
                )}
                {file.type === t('CHAT_PANEL.VIDEO') && (
                  <video src={file.url} controls className="w-full h-full object-contain rounded" />
                )}
                {file.type === t('CHAT_PANEL.AUDIO') && <audio src={file.url} controls className="w-full" />}
                {(file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')) && (
                  <PDFViewer
                    file={file.url}
                    showAll={false}
                    pageNumber={1}
                    width={250}
                    className="rounded border w-full h-full"
                  />
                )}
                {file.type === t('CHAT_PANEL.FILE') &&
                  file.type !== 'application/pdf' &&
                  !file.name?.toLowerCase().endsWith('.pdf') && (
                    <div className="w-16 h-16 rounded flex items-center justify-center">
                      <Paperclip size={20} />
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Banner */}
      {showError && (
        <div className="absolute flex items-center justify-between z-50 top-3 bg-warning-strong opacity-30 border-interactive text-inverse px-4 py-2 h-9 w-full animate-fadeIn">
          <p className="text-sm mt-1">
            {t('CHAT_PANEL.ERROR')} {errorMessage}
          </p>
        </div>
      )}

      {/* Chat Input */}
      {resolvedStatus[0]?.status !== constants.CHAT_PANEL.RESOLVED && (
        <div className="flex w-full border-t flex-row gap-2">
          <div ref={dropZoneRef} className="h-16 flex flex-1 items-center p-2 gap-3 relative bg z-20">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
              className="hidden"
            />

            <div className="flex flex-row items-center gap-1 w-full">
              {isPttActive ? (
                <div className="flex-1">{PttStatus}</div>
              ) : (
                <div
                  className={clsx(
                    'flex flex-row justify-between gap-1 items-center',
                    attachedFiles.length !== 0 ? 'w-full' : 'flex-1'
                  )}
                >
                  <div className="relative w-full flex-grow">
                    {/* Left icon - Attachment */}
                    <div
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground cursor-pointer"
                      onClick={triggerFileInput}
                    >
                      <Paperclip size={18} />
                    </div>

                    {/* Right icon - Send */}
                    <div
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground cursor-pointer"
                      onClick={handleSendMessage}
                    >
                      <Send size={18} />
                    </div>

                    {/* Input with padding to avoid overlap */}
                    <Input
                      className="pl-11 pr-9"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={
                        attachedFiles.length > 0
                          ? constants.CHAT_PANEL.INPUT_PLACEHOLDER_WITH_ATTACHMENT
                          : constants.CHAT_PANEL.INPUT_PLACEHOLDER_DEFAULT
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {(!isPttActive || !isPttLocked) && attachedFiles.length === 0 && (
            <div
              ref={micButtonRef}
              className="relative -top-6 z-50 right-2 flex items-center justify-center"
              style={{
                transform: pttButtonTransform,
                transition: 'transform 0.2s ease',
              }}
            >
              <PTTIcon
                size="lg"
                isDisabled={false}
                isActive={isPttActive || isPttLocked}
                onMouseDown={handleMicMouseDown}
                onMouseUp={handleMicMouseUp}
                onMouseMove={handleMicMouseMove}
                onMouseLeave={handleMicMouseLeave}
                onTouchStart={handleMicTouchStart}
                onTouchMove={handleMicTouchMove}
                onTouchEnd={handleMicTouchEnd}
              />
            </div>
          )}
        </div>
      )}

      {/* Floating Error Popup */}
      {showError && (
        <div className="fixed top-5 right-5 z-50 bg-error-strong text-inverse px-4 py-3 rounded-lg shadow-lg w-72 animate-fadeIn">
          <h3 className="font-semibold text-lg">{t('CHAT_PANEL.SOMETHING_WENT_WRONG')}</h3>
          <p className="text-sm mt-1">
            {t('CHAT_PANEL.ERROR')} {errorMessage}
          </p>
        </div>
      )}
    </div>
  );
};
